import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { PlayersAPI, TournamentsAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import GoldenParticles from "@/components/GoldenParticles";
import { GoldSpinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowRight,
  Plus,
  Zap,
  Sparkles,
  Wifi,
  ScanLine,
  Video,
  BarChart3,
  Users,
} from "lucide-react";
import { getSport } from "@/lib/sports";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import TeamFilter from "@/components/TeamFilter";
import CategoryFilter from "@/components/CategoryFilter";

const HERO_BG =
  "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=2200&q=80";

export default function Directory() {
  const [searchParams] = useSearchParams();
  const sportFilter = searchParams.get("sport");
  const teamFilter = searchParams.get("team");
  const { settings } = useSettings();
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = sportFilter ? { sport: sportFilter } : {};
      const [p, t] = await Promise.all([
        PlayersAPI.list(params),
        TournamentsAPI.list(),
      ]);
      setPlayers(p);
      setTournaments(t);
    } catch {
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sportFilter]);

  const seed = async () => {
    setSeeding(true);
    try {
      await PlayersAPI.seed();
      toast.success("Démo chargée");
      await load();
    } catch {
      toast.error("Échec du chargement de la démo");
    } finally {
      setSeeding(false);
    }
  };

  const totalGoals = players.reduce((sum, p) => {
    const sportDef = getSport(p.sport || "football");
    const primaryStat = sportDef.player_stats[1] || sportDef.player_stats[0];
    return sum + (p.stats?.[primaryStat.key] || 0);
  }, 0);
  const goalsLabel = sportFilter
    ? getSport(sportFilter).player_stats[1]?.label || "Stat principale"
    : "Buts cumulés";

  // Group players by club_name (using sport-filtered list)
  const clubs = useMemo(() => {
    const groups = new Map();
    (settings?.enabled_sports
      ? players.filter((p) => settings.enabled_sports.includes(p.sport || "football"))
      : players
    ).forEach((p) => {
      const key = p.club_name?.trim() || "Sans équipe";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return Array.from(groups.entries())
      .map(([name, list]) => ({
        name,
        list,
        logo: list.find((x) => x.club_logo_url)?.club_logo_url || "",
        accent: list[0]?.primary_color || "#0066FF",
      }))
      .sort((a, b) => b.list.length - a.list.length);
  }, [players, settings?.enabled_sports]);

  const [activeClub, setActiveClub] = useState("__all__");
  const [activeCategory, setActiveCategory] = useState("__all__");

  // Deep-link: ?team=NAME selects the corresponding club chip
  useEffect(() => {
    if (teamFilter && players.some((p) => (p.club_name?.trim() || "Sans équipe") === teamFilter)) {
      setActiveClub(teamFilter);
      const el = document.getElementById("roster");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [teamFilter, players]);

  // Pre-filter players by enabled_sports admin setting
  const enabledSports = settings?.enabled_sports;
  const sportFilteredPlayers = useMemo(() => {
    if (!enabledSports || enabledSports.length === 0) return players;
    return players.filter((p) => enabledSports.includes(p.sport || "football"));
  }, [players, enabledSports]);

  const visiblePlayers = useMemo(() => {
    let list = sportFilteredPlayers;
    if (activeClub !== "__all__") {
      list = list.filter(
        (p) => (p.club_name?.trim() || "Sans équipe") === activeClub,
      );
    }
    if (activeCategory !== "__all__") {
      list = list.filter(
        (p) => ((p.category || "").trim() || "Sans catégorie") === activeCategory,
      );
    }
    return list;
  }, [activeClub, activeCategory, sportFilteredPlayers]);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <SiteHeader showSportTabs={true} />

      {/* ========== HERO ========== */}
      <section className="relative min-h-[100vh] overflow-hidden">
        {/* BG image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${HERO_BG}')`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
            opacity: 0.32,
          }}
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 60%, #000 100%)",
          }}
        />
        {/* Gold halo */}
        <div
          className="absolute -top-[300px] left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: "#D4AF37" }}
        />
        <div className="absolute inset-0 grain pointer-events-none opacity-50" />
        <GoldenParticles count={16} density="normal" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pt-20 md:pt-28 pb-20">
          {/* Top badge */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-3 border border-[#D4AF37]/30 bg-black/40 backdrop-blur-xl rounded-full px-5 py-2 mb-10"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4AF37]" />
            </span>
            <span className="text-[#D4AF37] uppercase tracking-[0.35em] text-[10px] md:text-[11px] font-bold">
              NFC + QR · Connected Trophies
            </span>
          </motion.div>

          {/* Hero typo */}
          <div className="max-w-4xl">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-semibold uppercase tracking-tight leading-[0.88] text-5xl sm:text-7xl md:text-[7.5rem]"
            >
              <span className="block text-white">Le Trophée</span>
              <span
                className="block text-gradient-gold"
                style={{
                  filter: "drop-shadow(0 4px 24px rgba(212,175,55,0.35))",
                }}
              >
                Continue
              </span>
              <span className="block text-zinc-400 italic font-light tracking-tight">
                à vivre.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 max-w-2xl text-zinc-400 text-base md:text-xl font-body font-light leading-relaxed"
            >
              Laser Tech Création transforme vos trophées et médailles en
              souvenirs digitaux permanents — gravure laser, QR code et puce
              NFC pour révéler stats, vidéos et photos d'un simple geste.
            </motion.p>

            {/* CTA buttons */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="flex flex-wrap gap-3 mt-10"
            >
              <a href="#roster" data-testid="cta-see-players">
                <Button
                  className="h-14 px-8 rounded-2xl text-black font-bold text-sm uppercase tracking-[0.18em] transition-transform hover:scale-[1.03]"
                  style={{
                    background:
                      "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)",
                    boxShadow: "0 12px 40px rgba(212,175,55,0.3)",
                  }}
                >
                  Voir les joueurs
                </Button>
              </a>
              {user && (
                <Link to="/admin" data-testid="cta-create-player">
                  <Button
                    variant="outline"
                    className="h-14 px-7 rounded-2xl border-white/15 bg-white/[0.03] backdrop-blur-xl text-white hover:bg-white/[0.07] hover:border-white/25 font-semibold text-sm uppercase tracking-[0.15em]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un joueur
                  </Button>
                </Link>
              )}
              {players.length === 0 && user && (
                <Button
                  variant="ghost"
                  data-testid="cta-seed-demo"
                  onClick={seed}
                  disabled={seeding}
                  className="h-14 px-5 rounded-2xl text-zinc-500 hover:text-white hover:bg-white/5"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {seeding ? "Création..." : "Charger un exemple"}
                </Button>
              )}
            </motion.div>

            {/* Features grid */}
            {settings?.show_features_grid !== false && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-14 md:mt-16"
              >
                {[
                  { icon: ScanLine, label: "QR Gravé", note: "Haute densité" },
                  { icon: Wifi, label: "NFC Intégré", note: "Tap to open" },
                  { icon: Video, label: "Vidéos & Photos", note: "Galerie joueur" },
                  { icon: BarChart3, label: "Stats Tournoi", note: "Classement live" },
                ].map((f, i) => (
                  <FeatureChip key={i} {...f} delay={0.5 + i * 0.05} />
                ))}
              </motion.div>
            )}
          </div>

          {/* Mini stats counter */}
          {settings?.show_stats_counter !== false && (
            <div className="flex flex-wrap gap-8 md:gap-14 pt-16 mt-16 border-t border-white/5">
              <Stat label="Joueurs" value={players.length} />
              <Stat label="Équipes" value={clubs.length} />
              <Stat label={goalsLabel} value={totalGoals} accent="#D4AF37" />
            </div>
          )}
        </div>
      </section>

      {/* ========== DERNIÈRES MÉDAILLES (carousel) ========== */}
      {!loading && players.length > 0 && settings?.show_recent_medals && (
        <section
          className="max-w-7xl mx-auto w-full px-5 md:px-10 pt-4 pb-12 md:pb-16"
          data-testid="recent-medals-section"
        >
          <div className="flex items-end justify-between mb-6">
            <div>
              <div
                className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2"
                style={{ color: "#D4AF37" }}
              >
                Atelier · Dernières gravures
              </div>
              <h2 className="font-display text-2xl md:text-4xl font-semibold uppercase tracking-tight">
                Dernières médailles gravées
              </h2>
            </div>
          </div>
          <div
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 md:-mx-10 md:px-10 scroll-smooth"
            data-testid="recent-medals-carousel"
          >
            {players.slice(0, 8).map((p, i) => {
              const sportDef = getSport(p.sport || "football");
              const accent = p.primary_color || sportDef.color;
              return (
                <Link
                  key={p.id}
                  to={`/player/${p.slug}`}
                  data-testid={`recent-medal-${p.slug}`}
                  className="snap-start shrink-0 w-[260px] md:w-[300px] group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#15161A] to-[#08090C] hover:-translate-y-1 hover:border-white/30 transition-all duration-300"
                  style={{ boxShadow: "0 18px 60px -25px rgba(0,0,0,0.8)" }}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-[2px] z-10"
                    style={{
                      background: `linear-gradient(90deg, transparent 0%, ${accent} 50%, transparent 100%)`,
                    }}
                  />
                  <div className="relative aspect-[3/4] overflow-hidden">
                    {p.hero_image_url ? (
                      <img
                        src={p.hero_image_url}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] flex items-center justify-center">
                        <span className="font-display text-5xl text-zinc-700">{p.name?.[0]}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20">
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-white"
                        style={{ boxShadow: "0 0 8px #FFFFFF" }}
                      />
                      <span className="text-[10px] uppercase tracking-[0.25em] font-bold metal-text">
                        {sportDef.label}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3.5">
                      <div
                        className="text-[10px] font-bold uppercase tracking-[0.25em] mb-1 truncate"
                        style={{ color: accent }}
                      >
                        {p.club_name || p.position}
                      </div>
                      <div className="font-display text-2xl font-semibold uppercase leading-[0.95] tracking-tight truncate">
                        {p.name}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
            <div className="snap-start shrink-0 w-4 md:w-6" />
          </div>
        </section>
      )}

      {/* ========== ROSTER ========== */}
      <section
        id="roster"
        className="max-w-7xl mx-auto w-full px-5 md:px-10 py-20 md:py-28"
      >
        <div className="flex items-end justify-between mb-8 md:mb-10 flex-wrap gap-4">
          <div>
            <div
              className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2"
              style={{ color: "#D4AF37" }}
            >
              Effectif
            </div>
            <h2 className="font-display text-3xl md:text-6xl font-semibold uppercase tracking-tight">
              Vos cartes joueur
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {user && (
              <Link to="/admin" data-testid="roster-add-player">
                <Button
                  size="sm"
                  className="bg-[#D4AF37] hover:bg-[#B8941F] text-black rounded-full h-9 px-4 text-xs uppercase tracking-[0.18em] font-bold"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Joueur
                </Button>
              </Link>
            )}
            <span className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-body">
              {players.length} joueur{players.length > 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Filters: Team + Category */}
        {!loading && players.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8 md:mb-10">
            {clubs.length > 0 && (
              <TeamFilter
                clubs={clubs}
                players={sportFilteredPlayers}
                activeClub={activeClub}
                onChange={setActiveClub}
              />
            )}
            <CategoryFilter
              players={sportFilteredPlayers}
              activeCategory={activeCategory}
              onChange={setActiveCategory}
            />
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-3 text-zinc-500 font-body py-6" data-testid="loading-state">
            <GoldSpinner size={20} />
            <span className="text-[10px] uppercase tracking-[0.3em]">Chargement</span>
          </div>
        ) : players.length === 0 ? (
          <div
            className="border border-dashed border-white/10 rounded-2xl p-14 text-center"
            data-testid="empty-state"
          >
            <div
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{
                background: "linear-gradient(180deg, #1a1a1a 0%, #0A0A0A 100%)",
                border: "1px solid rgba(212,175,55,0.4)",
              }}
            >
              <Sparkles className="w-7 h-7" style={{ color: "#D4AF37" }} />
            </div>
            <p className="font-body text-zinc-400">
              Aucun joueur pour l'instant. Crée le premier !
            </p>
          </div>
        ) : activeClub === "__all__" ? (
          /* Grouped view by club */
          <div className="space-y-12 md:space-y-16">
            {clubs.map((c) => (
              <div key={c.name} data-testid={`club-section-${c.name}`}>
                <ClubHeader club={c} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {c.list.map((p, i) => (
                    <PlayerCard key={p.id} player={p} index={i} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Filtered flat view */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {visiblePlayers.map((p, i) => (
              <PlayerCard key={p.id} player={p} index={i} />
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-zinc-600 font-body uppercase tracking-[0.25em]">
        <img
          src="/laser-tech-logo.png"
          alt="Laser Tech"
          className="h-14 mx-auto mb-3"
          style={{ mixBlendMode: "screen" }}
        />
        Laser Tech · Création · Genève
      </footer>
    </div>
  );
}

/* ===================== sub-components ===================== */

const Stat = ({ label, value, accent = "#FFFFFF" }) => (
  <div>
    <div
      className="font-display text-3xl md:text-4xl font-semibold leading-none tabular-nums"
      style={{ color: accent }}
    >
      {value}
    </div>
    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500 mt-1">
      {label}
    </div>
  </div>
);

const FeatureChip = ({ icon: Icon, label, note }) => (
  <div className="border border-white/10 bg-white/[0.03] backdrop-blur-xl rounded-2xl px-4 py-4 hover:border-[#D4AF37]/30 hover:bg-white/[0.05] transition-all">
    <div className="flex items-center gap-2.5">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{
          background: "rgba(212,175,55,0.12)",
          border: "1px solid rgba(212,175,55,0.25)",
        }}
      >
        <Icon className="w-4 h-4" style={{ color: "#D4AF37" }} />
      </div>
      <div className="min-w-0">
        <div className="text-white font-semibold text-sm leading-tight">{label}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5 truncate">
          {note}
        </div>
      </div>
    </div>
  </div>
);

const ClubChip = ({ label, logo, accent = "#FFFFFF", count, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`group inline-flex items-center gap-2 rounded-full pl-2 pr-3 py-1.5 border transition-all text-xs font-body font-medium ${
      active
        ? "bg-white text-black border-white"
        : "bg-white/[0.03] text-zinc-300 border-white/10 hover:border-white/30 hover:bg-white/[0.06]"
    }`}
    style={active ? { backgroundColor: accent, color: "#0A0A0A", borderColor: accent } : {}}
  >
    {logo ? (
      <img
        src={logo}
        alt=""
        className="w-6 h-6 rounded-full bg-black/40 object-contain p-0.5 border border-white/10"
      />
    ) : (
      <span
        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
        style={{ background: `${accent}22`, color: active ? "#0A0A0A" : accent }}
      >
        {label === "Toutes les équipes" ? <Users className="w-3 h-3" /> : label[0]}
      </span>
    )}
    <span className="uppercase tracking-[0.12em] text-[11px] truncate max-w-[180px]">
      {label}
    </span>
    <span
      className={`ml-1 text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${
        active ? "bg-black/15 text-black/80" : "bg-white/5 text-zinc-400"
      }`}
    >
      {count}
    </span>
  </button>
);

const ClubHeader = ({ club }) => (
  <div className="flex items-center gap-3 mb-5">
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 border border-white/10 overflow-hidden"
      style={{ background: `${club.accent}22` }}
    >
      {club.logo ? (
        <img src={club.logo} alt={club.name} className="w-full h-full object-contain p-1" />
      ) : (
        <span className="font-display font-semibold text-sm" style={{ color: club.accent }}>
          {club.name[0]}
        </span>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div
        className="text-[10px] font-bold uppercase tracking-[0.28em] truncate"
        style={{ color: club.accent }}
      >
        Équipe
      </div>
      <div className="font-display text-xl md:text-2xl font-semibold uppercase tracking-tight truncate">
        {club.name}
      </div>
    </div>
    <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-body shrink-0">
      {club.list.length} joueur{club.list.length > 1 ? "s" : ""}
    </span>
  </div>
);

const PlayerCard = ({ player: p, index }) => {
  const accent = p.primary_color || "#0066FF";
  const sportDef = getSport(p.sport || "football");
  const headlineStat = sportDef.player_stats[1] || sportDef.player_stats[0];
  const headlineValue = p.stats?.[headlineStat.key] ?? 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link
        to={`/player/${p.slug}`}
        data-testid={`player-card-${p.slug}`}
        className="group block relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#15161A] to-[#08090C] hover:-translate-y-1 hover:border-white/30 transition-all duration-300"
        style={{ boxShadow: "0 18px 60px -25px rgba(0,0,0,0.8)" }}
      >
        <div
          className="absolute top-0 left-0 right-0 h-[2px] z-10"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${accent} 50%, transparent 100%)`,
          }}
        />

        <div className="relative aspect-[4/5] overflow-hidden">
          {p.hero_image_url ? (
            <img
              src={p.hero_image_url}
              alt={p.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] flex items-center justify-center">
              <span className="font-display text-6xl text-zinc-700">{p.name?.[0]}</span>
            </div>
          )}

          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(5,5,5,0) 35%, rgba(5,5,5,0.55) 65%, rgba(5,5,5,0.95) 100%)",
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: `radial-gradient(70% 50% at 50% 100%, ${accent}33 0%, rgba(5,5,5,0) 70%)`,
            }}
          />

          {p.jersey_number != null && (
            <div
              className="absolute top-3 left-4 font-display text-5xl font-semibold leading-none"
              style={{
                color: accent,
                filter: `drop-shadow(0 0 18px ${accent}99)`,
              }}
            >
              {p.jersey_number}
            </div>
          )}

          {p.club_logo_url && (
            <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-md border border-white/15 p-1.5 overflow-hidden">
              <img src={p.club_logo_url} alt={p.club_name} className="w-full h-full object-contain" />
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div
              className="text-[10px] font-bold uppercase tracking-[0.25em] mb-1"
              style={{ color: accent }}
            >
              {p.position}
              {p.category ? ` · ${p.category}` : ""}
            </div>
            <div className="font-display text-3xl md:text-[2rem] font-semibold uppercase leading-[0.95] tracking-tight">
              {p.name}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 flex items-center justify-between border-t border-white/5">
          <div className="flex items-center gap-3 text-xs font-body text-zinc-400 truncate min-w-0">
            <span className="truncate">{p.club_name || "—"}</span>
          </div>
          <div className="flex items-center gap-3 text-xs font-body text-zinc-300 shrink-0">
            <span className="tabular-nums" style={{ color: accent }}>
              {headlineValue}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              {headlineStat.label}
            </span>
            <ArrowRight
              className="w-4 h-4 group-hover:translate-x-1 transition-transform"
              style={{ color: accent }}
            />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
